import { DeployEnvironment, envConfig } from '@globals/env'
import { lazyWithRetries } from '@globals/lazyWithRetries'
import AppPaths from '@globals/paths'
import guestRoutes from '@guest/routes'
import ConstrainedWidthLayout from '@layouts/ConstrainedWidthLayout'
import { RouteUpdateProvider } from '@layouts/RouteUpdateContext'
import PermissionDeniedUI from '@shared/permissions/PermissionDeniedUI'
import NotFound from '@shared/routing/NotFound'
import RouteLoading from '@shared/routing/RouteLoading'
import tenantScopeRoutes from '@tenant/routes'
import { Suspense } from 'react'
import { Outlet, RouteObject, ScrollRestoration } from 'react-router'
import { AwaitAuth } from './AwaitAuth'
import DocumentTitleWrapper from './DocumentTitleWrapper'
import { GuestRoutes } from './GuestRoutes'
import RouteReloader from './RouteReloader'
import { TenantScopeRoutes } from './TenantScopeRoutes'
import { UserScopeRoutes } from './UserScopeRoutes'
import AppRoutesErrorElement from './errors'
import userScopeRoutes from './protected/user/routes'

const FeatureFlags = lazyWithRetries(() => import('@guest/featureFlags'))
const KudosTvRoute = lazyWithRetries(() => import('@guest/kudosTv'))
const MaintenanceRoute = lazyWithRetries(() => import('@guest/maintenance'))
const TenantLoginRoute = lazyWithRetries(() => import('@guest/tenantLogin'))
const Logout = lazyWithRetries(() => import('./protected/logout'))

const appRoutes: RouteObject[] = [
  {
    element: (
      <>
        <ScrollRestoration />
        <RouteReloader />
        <Outlet />
      </>
    ),
    errorElement: <AppRoutesErrorElement />,
    children: [
      {
        path: AppPaths.anon.tenantLogin(),
        element: (
          <Suspense fallback={<RouteLoading />}>
            <TenantLoginRoute />
          </Suspense>
        ),
      },
      {
        path: AppPaths.anon.watchKudosTv(':tvToken'),
        element: (
          <Suspense fallback={<RouteLoading />}>
            <KudosTvRoute />
          </Suspense>
        ),
      },
      {
        path: AppPaths.anon.maintenance(),
        element: (
          <ConstrainedWidthLayout>
            <Suspense fallback={<RouteLoading />}>
              <MaintenanceRoute />
            </Suspense>
          </ConstrainedWidthLayout>
        ),
      },
      {
        element: (
          <RouteUpdateProvider>
            <DocumentTitleWrapper>
              <AwaitAuth />
            </DocumentTitleWrapper>
          </RouteUpdateProvider>
        ),
        children: [
          // Unauthenticated routes
          {
            element: <GuestRoutes />,
            children: guestRoutes,
          },
          // Authenticated Routes
          {
            path: AppPaths.logout(),
            element: (
              <Suspense fallback={<RouteLoading />}>
                <Logout />
              </Suspense>
            ),
          },
          {
            element: <UserScopeRoutes />,
            children: userScopeRoutes,
          },
          {
            element: <TenantScopeRoutes />,
            children: tenantScopeRoutes,
          },
        ],
      },
      {
        path: AppPaths.anon.accessDenied(),
        element: <PermissionDeniedUI />,
      },
      {
        path: AppPaths.anon.featureFlags(),
        element:
          envConfig.DEPLOY_ENVIRONMENT !== DeployEnvironment.PRODUCTION ? (
            <Suspense fallback={<RouteLoading />}>
              <FeatureFlags />
            </Suspense>
          ) : (
            <NotFound to={AppPaths.tenant.home()} />
          ),
      },
      {
        path: AppPaths.anon.notFound(),
        element: <NotFound to={AppPaths.tenant.home()} />,
      },
      {
        path: '*',
        element: <NotFound to={AppPaths.tenant.home()} />,
      },
    ],
  },
]

export default appRoutes
